import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const HALLS = ["I", "II", "III", "IV"];
const COLORS = {
  approved: { bg: "#F3E3C3", fg: "#7A5A1E", label: "Approved · awaiting payment" },
  paid: { bg: "#4A5D4E", fg: "#F9F8F6", label: "Paid · public" },
  published: { bg: "#1C1C19", fg: "#F9F8F6", label: "In-house exhibition" },
  draft: { bg: "#D9D6CE", fg: "#1C1C19", label: "In-house (draft)" },
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Greedy lane packing so overlapping bars in one row stack instead of hiding each other.
function packLanes(items) {
  const lanes = [];
  [...items].sort((a, b) => a.start_date.localeCompare(b.start_date)).forEach((it) => {
    const lane = lanes.find((l) => l[l.length - 1].end_date < it.start_date);
    lane ? lane.push(it) : lanes.push([it]);
  });
  return lanes.length ? lanes : [[]];
}

function Bar({ item, days, onOpen }) {
  const first = days[0], last = days[days.length - 1];
  const s = Math.max(0, days.indexOf(item.start_date < first ? first : item.start_date));
  const e = days.indexOf(item.end_date > last ? last : item.end_date);
  const c = COLORS[item.status] || COLORS.draft;
  const label = item.kind === "application" ? `${item.reference} · ${item.title}` : item.title;
  const click = () => (item.kind === "application" ? onOpen(item.id) : window.open(`/events/${item.slug}`, "_blank"));
  return (
    <button type="button" onClick={click} title={`${label} · ${item.start_date} → ${item.end_date}`}
      className="absolute top-1 bottom-1 px-2 text-[11px] truncate text-left hover:opacity-90 transition-opacity"
      style={{ left: `${(s / days.length) * 100}%`, width: `${((e - s + 1) / days.length) * 100}%`, background: c.bg, color: c.fg }}
      data-testid={`calendar-bar-${item.reference || item.slug}`}>
      {label}
    </button>
  );
}

function Row({ label, items, days, onOpen, testId }) {
  return packLanes(items).map((lane, i) => (
    <div key={i} className="flex border-t border-soft" data-testid={i === 0 ? testId : undefined}>
      <div className="w-36 shrink-0 p-2 text-xs text-muted2">{i === 0 ? label : ""}</div>
      <div className="relative flex-1 h-9">
        <div className="absolute inset-0 flex">
          {days.map((d) => {
            const wd = new Date(d + "T00:00:00").getDay();
            return <div key={d} className="flex-1 border-l border-soft/60" style={wd === 1 || wd === 2 ? { background: "repeating-linear-gradient(45deg,#F5F3EC,#F5F3EC 3px,#EEECE6 3px,#EEECE6 6px)" } : {}} />;
          })}
        </div>
        {lane.map((it) => <Bar key={`${it.id}-${it.start_date}`} item={it} days={days} onOpen={onOpen} />)}
      </div>
    </div>
  ));
}

export default function GalleryCalendar({ onOpen, refreshKey }) {
  const [month, setMonth] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [items, setItems] = useState([]);
  const days = useMemo(() => {
    const out = [];
    for (let d = new Date(month); d.getMonth() === month.getMonth(); d.setDate(d.getDate() + 1)) out.push(iso(d));
    return out;
  }, [month]);

  useEffect(() => {
    api.get("/admin/gallery/calendar", { params: { start_date: days[0], end_date: days[days.length - 1] } })
      .then((r) => setItems(r.data.items)).catch(() => {});
  }, [days, refreshKey]);

  const shift = (n) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + n, 1));
  const inHouse = items.filter((i) => i.kind === "event");

  return (
    <section className="mt-8 border border-soft bg-white" data-testid="gallery-calendar">
      <div className="flex items-center justify-between p-4">
        <button onClick={() => shift(-1)} className="p-2 hover:bg-cream-2" data-testid="calendar-prev"><ChevronLeft size={18} /></button>
        <div className="font-serif text-2xl" data-testid="calendar-month">{month.toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
        <button onClick={() => shift(1)} className="p-2 hover:bg-cream-2" data-testid="calendar-next"><ChevronRight size={18} /></button>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-[1000px]">
          <div className="flex">
            <div className="w-36 shrink-0" />
            <div className="flex-1 flex">
              {days.map((d) => {
                const dt = new Date(d + "T00:00:00");
                const closed = dt.getDay() === 1 || dt.getDay() === 2;
                return (
                  <div key={d} className={`flex-1 text-center text-[10px] py-1 ${closed ? "text-muted2/60" : "text-muted2"}`}>
                    <div>{dt.toLocaleDateString("en-IN", { weekday: "short" }).slice(0, 2)}</div>
                    <div className="text-xs text-charcoal">{dt.getDate()}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {HALLS.map((h) => (
            <Row key={h} label={`Gallery ${h}`} items={items.filter((i) => i.kind === "application" && i.halls.includes(h))} days={days} onOpen={onOpen} testId={`calendar-row-${h}`} />
          ))}
          <Row label="In-house" items={inHouse} days={days} onOpen={onOpen} testId="calendar-row-inhouse" />
        </div>
      </div>
      <div className="flex flex-wrap gap-4 p-4 text-[11px] text-muted2 border-t border-soft">
        {Object.entries(COLORS).map(([k, c]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: c.bg }} />{c.label}</span>
        ))}
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 inline-block" style={{ background: "repeating-linear-gradient(45deg,#F5F3EC,#F5F3EC 2px,#EEECE6 2px,#EEECE6 4px)" }} />Closed (Mon, Tue)</span>
      </div>
    </section>
  );
}
